import React, { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Clock, Search, Sparkles } from 'lucide-react';
import { getParentBlogBySlug, parentBlogs } from '../../data/parentBlogs';
import socialPlayImg from '../../assets/images/parent-domains/social-play.jpg';
import communicationImg from '../../assets/images/parent-domains/communication.jpg';
import cognitiveImg from '../../assets/images/parent-domains/cognitive.jpg';
import motorSkillsImg from '../../assets/images/parent-domains/motor-skills.jpg';
import dailySkillsImg from '../../assets/images/parent-domains/daily-skills.jpg';
import emotionalRegulationImg from '../../assets/images/parent-domains/emotional-regulation.jpg';
import schoolReadinessImg from '../../assets/images/parent-domains/school-readiness.jpg';
import sensoryImg from '../../assets/images/parent-domains/sensory.jpg';

const heroMap = {
  social: socialPlayImg,
  communication: communicationImg,
  cognitive: cognitiveImg,
  motor: motorSkillsImg,
  daily: dailySkillsImg,
  emotional: emotionalRegulationImg,
  school: schoolReadinessImg,
  sensory: sensoryImg,
} as const;

function extractUrl(text: string): string | null {
  const match = text.match(/https?:\/\/[^\s]+/i);
  return match ? match[0] : null;
}

function renderParagraphWithLinks(text: string): React.ReactNode {
  const parts = text.split(/(https?:\/\/[^\s]+)/gi);
  return parts.map((part, i) =>
    /^https?:\/\//i.test(part) ? (
      <a
        key={`link-${i}`}
        href={part}
        target="_blank"
        rel="noreferrer"
        className="font-semibold text-indigo-600 underline decoration-indigo-300 underline-offset-2 hover:text-indigo-800"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

export default function ParentBlogArticlePage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const blog = useMemo(() => getParentBlogBySlug(slug), [slug]);

  if (!blog) {
    return (
      <div className="min-h-screen bg-slate-50 px-4 py-16">
        <div className="mx-auto max-w-3xl rounded-2xl bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-semibold text-slate-900">Article not found</h1>
          <p className="mt-2 text-slate-600">This article may have moved.</p>
          <button
            type="button"
            onClick={() => navigate('/parents')}
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Blog
          </button>
        </div>
      </div>
    );
  }

  const heroImage = heroMap[blog.heroImage];
  const paragraphs = blog.content.split('\n\n');
  const related = parentBlogs.filter((item) => item.slug !== blog.slug).slice(0, 3);

  return (
    <div className="min-h-screen bg-slate-50">
      <section className="w-full bg-gradient-to-r from-indigo-50 via-rose-50 to-amber-50">
        <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8 lg:py-14">
          <Link to="/parents" className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
            Back to Parent Hub
          </Link>
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <div className="text-left">
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="sr-only">Category: {blog.category}</span>
                <span className="inline-flex items-center gap-1 text-slate-600">
                  <Clock className="h-4 w-4" />
                  {blog.readTime}
                </span>
              </div>
              <h1 className="mt-4 text-4xl font-bold leading-tight text-slate-900 lg:text-5xl">{blog.title}</h1>
            </div>
            <div className="overflow-hidden rounded-3xl shadow-md">
              <img src={heroImage} alt={blog.title} className="h-72 w-full object-cover lg:h-80" />
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-white">
        <div className="mx-auto max-w-3xl px-6 py-10 lg:py-14">
          <div className="space-y-5 text-left">
            {paragraphs.map((paragraph, idx) => {
              const trimmed = paragraph.trim();
              if (!trimmed) return null;
              const url = extractUrl(trimmed);

              if (trimmed.toLowerCase().startsWith('try this this week:')) {
                return (
                  <div key={`${blog.slug}-try-${idx}`} className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-left">
                    <p className="text-lg font-semibold leading-8 text-amber-900">{renderParagraphWithLinks(trimmed)}</p>
                  </div>
                );
              }

              if (url && trimmed === url) {
                return (
                  <div key={`${blog.slug}-cta-${idx}`} className="pt-1">
                    <a
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700"
                    >
                      Open TinyWins Resource
                      <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                );
              }

              return (
                <p key={`${blog.slug}-p-${idx}`} className="text-lg leading-9 text-slate-700">
                  {renderParagraphWithLinks(trimmed)}
                </p>
              );
            })}
          </div>
        </div>
      </section>

      <section className="w-full bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600">
        <div className="mx-auto max-w-7xl px-6 py-10 text-left lg:px-8">
          <h2 className="text-3xl font-bold text-white">Ready for the next step?</h2>
          <p className="mt-3 max-w-3xl text-indigo-100">
            Book support, try tools, and continue progress at home with a plan that fits your family.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/organizations" className="rounded-full bg-white px-6 py-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
              Book a Therapist
            </Link>
            <button
              type="button"
              onClick={() => navigate('/parents#free-tools')}
              className="rounded-full bg-indigo-500 px-6 py-3 text-sm font-semibold text-white hover:bg-indigo-400"
            >
              Try Free Tools
            </button>
          </div>
        </div>
      </section>

      <section className="w-full bg-slate-50">
        <div className="mx-auto max-w-7xl px-6 py-10 lg:px-8">
          <h2 className="text-left text-2xl font-semibold">Related articles</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            {related.map((item) => (
              <article key={item.slug} className="rounded-2xl bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold">{item.title}</p>
                <p className="mt-2 line-clamp-3 text-sm text-slate-600">{item.intro}</p>
                <Link to={`/parents/blog/${item.slug}`} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:text-indigo-800">
                  Read guide
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </article>
            ))}
          </div>
          <Link to="/parents" className="mt-8 inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-100">
            <ArrowLeft className="h-4 w-4" />
            Back to Parent Hub
          </Link>
        </div>
      </section>
    </div>
  );
}
